// frontend/src/App.js
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Analytics } from '@vercel/analytics/react';
import { UIProvider } from './contexts/UIContext';
import AdminPage from './pages/AdminPage';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import StatusConfirmationPage from './pages/StatusConfirmationPage';


// Lazy load Layout component
const Layout = lazy(() => import('./layouts/Layout'));

// Lazy load pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const GcFeedbackPage = lazy(() => import('./pages/GcFeedbackPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const GcPage = lazy(() => import('./pages/GcPage'));
const NdaPage = lazy(() => import('./pages/NdaPage'));
const TosPage = React.lazy(() => import('./pages/TosPage'));
const SecurityFaqPage = lazy(() => import('./pages/SecurityFaqPage'));
const GcDetailPage = lazy(() => import('./pages/GcDetailPage'));
const ChartsPage = lazy(() => import('./pages/ChartsPage'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));

// --- Bid Calendar Page ---
const BidCalendarPage = lazy(() => import('./pages/BidCalendarPage'));

// --- Tutorial Pages ---
const LearnPage = lazy(() => import('./pages/LearnPage'));
const TutorialsPage = lazy(() => import('./pages/TutorialsPage'));

// --- Bid Pages ---
const BidEditPage = lazy(() => import('./pages/BidEditPage'));
const BidCreatePage = lazy(() => import('./pages/BidCreatePage'));
const UpcomingBidCreatePage = lazy(() => import('./pages/UpcomingBidCreatePage'));
const UpcomingBidEditPage = lazy(() => import('./pages/UpcomingBidEditPage'));


// This helper component checks authentication and renders the shared Layout (with Outlet)
// If not authenticated, it redirects to the login page.
function ProtectedRoutesLayout() {
    const { isAuthenticated, user } = useAuth();
    const location = useLocation();
    

    // If user needs TOS and not already on TOS page, redirect
    if (isAuthenticated && user?.requiresTosAcceptance && location.pathname !== '/tos') {
        return <Navigate to="/tos" state={{ email: user.email, existingUser: true }} replace />;
    }

    return isAuthenticated ? (
        <Suspense fallback={<CircularProgress />}>
            <Layout />
        </Suspense>
    ) : <Navigate to="/login" replace />;
}

function App() {
    const { isAuthenticated, isLoading } = useAuth(); // Get auth state from context

    // Show branded loading indicator while checking auth status
    if (isLoading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fff',
                    padding: 3,
                }}
            >
                <Box
                    sx={{
                        mb: 4,
                        width: '100%',
                        maxWidth: '250px',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <img
                        src="/logo.png"
                        alt="BidTransparency Logo"
                        style={{
                            width: '100%',
                            height: 'auto',
                        }}
                    />
                </Box>
                <CircularProgress size={40} />
            </Box>
        );
    }

    return (
        <UIProvider>
    <>
                <Routes>
                    <Route
                        path="/admin"
                        element={
                            <ProtectedAdminRoute>
                                <AdminPage />
                            </ProtectedAdminRoute>
                        }
                    />
                    {/* --- Public Routes --- */}

                    { }
                <Route path="/" element={
                    <Suspense fallback={<CircularProgress />}>
                        <LandingPage />
                    </Suspense>
                } />
                
                    <Route path="/feedback/:token" element={
                        <Suspense fallback={<CircularProgress />}>
                            <GcFeedbackPage />
                        </Suspense>
                    } />

                    <Route path="/confirm-status/:token" element={
                        <Suspense fallback={<CircularProgress />}>
                            <StatusConfirmationPage />
                        </Suspense>
                    } />

                    {/* Security FAQ Route - Public access */}
                    <Route path="/security-faq" element={
                        <Suspense fallback={<CircularProgress />}>
                            <SecurityFaqPage />
                        </Suspense>
                    } />

                    {/* NDA Route */}
                    <Route path="/nda" element={
                        <Suspense fallback={<CircularProgress />}>
                            <NdaPage />
                        </Suspense>
                    } />

                    {/* TOS Route */}
                    <Route path="/tos" element={
                        <Suspense fallback={<CircularProgress />}>
                            <TosPage />
                        </Suspense>
                    } />

                    {/* Login Route: If user is already logged in, redirect them */}
                    <Route
                        path="/login"
                        element={isAuthenticated ?
                            <Navigate to="/dashboard" replace /> :
                            <Suspense fallback={<CircularProgress />}>
                                <LoginPage />
                            </Suspense>
                        }
                    />
                
                <Route path="/register" element={
                    <Suspense fallback={<CircularProgress />}>
                        <RegisterPage />
                    </Suspense>
                } />

                    {/* Learn Page - Public tutorials */}
                    <Route path="/learn" element={
                        <Suspense fallback={<CircularProgress />}>
                            <LearnPage />
                        </Suspense>
                    } />

                    {/* --- Protected Routes --- */}
                    <Route element={
                        <Suspense fallback={<CircularProgress />}>
                            <ProtectedRoutesLayout />
                        </Suspense>
                }>
                        {/* Child routes render inside Layout's <Outlet /> */}
                        <Route path="dashboard" element={
                            <Suspense fallback={<CircularProgress />}>
                                <DashboardPage />
                            </Suspense>
                        } />
                         <Route path="projects" element={
                            <Suspense fallback={<CircularProgress />}>
                                <ProjectsPage />
                            </Suspense>
                        } />
                        <Route path="projects/:projectId" element={
                            <Suspense fallback={<CircularProgress />}>
                                <ProjectDetailPage />
                            </Suspense>
                        } />
                        <Route path="gcs" element={
                            <Suspense fallback={<CircularProgress />}>
                                <GcPage />
                            </Suspense>
                        } />
                        <Route path="gcs/:gcId" element={
                            <Suspense fallback={<CircularProgress />}>
                                <GcDetailPage />
                            </Suspense>
                        } />
                        {/* --- NEW: Bid Calendar Routes --- */}
                        <Route path="bid-calendar" element={
                            <Suspense fallback={<CircularProgress />}>
                                <BidCalendarPage />
                            </Suspense>
                        } />
                        <Route path="charts" element={
                            <Suspense fallback={<CircularProgress />}>
                                <ChartsPage />
                            </Suspense>
                        } />
                        <Route path="schedule" element={
                            <Suspense fallback={<CircularProgress />}>
                                <SchedulePage />
                            </Suspense>
                        } />
                        {/* Redirect old route to new standalone page */}
                        <Route path="add-upcoming-bid" element={<Navigate to="/bids/upcoming/new" replace />} />
                        {/* You might later add a route for managing contacts of a specific GC: */}
                        {/* <Route path="gcs/:gcId/contacts-management" element={<GcContactsManagementPage />} /> */}
                        <Route path="account" element={
                            <Suspense fallback={<CircularProgress />}>
                                <AccountPage />
                            </Suspense>
                        } />
                        <Route path="users" element={
                            <Suspense fallback={<CircularProgress />}>
                                <UserManagementPage />
                            </Suspense>
                        } />
                        <Route path="tutorials" element={
                            <Suspense fallback={<CircularProgress />}>
                                <TutorialsPage />
                            </Suspense>
                        } />
                        {/* Bid Routes */}
                        <Route path="bids/new" element={
                            <Suspense fallback={<CircularProgress />}>
                                <BidCreatePage />
                            </Suspense>
                        } />
                        <Route path="bids/:bidId/edit" element={
                            <Suspense fallback={<CircularProgress />}>
                                <BidEditPage />
                            </Suspense>
                        } />
                        {/* Upcoming Bid Routes */}
                        <Route path="bids/upcoming/new" element={
                            <Suspense fallback={<CircularProgress />}>
                                <UpcomingBidCreatePage />
                            </Suspense>
                        } />
                        <Route path="bids/upcoming/:id/edit" element={
                            <Suspense fallback={<CircularProgress />}>
                                <UpcomingBidEditPage />
                            </Suspense>
                        } />
                </Route>

                
                {/* --- Fallback Route --- */}
                <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />} />
        </Routes>
        <Analytics />
    </>
    </UIProvider>
    );
}

export default App;